import React, { useState, useMemo } from 'react';
import {
    ChevronLeft, ChevronRight, Plus, MoreHorizontal,
    Clock, CheckCircle2, AlertCircle, TrendingUp,
    Calendar, Users, Target, Zap, ArrowUpRight,
    Circle, Timer, Flag
} from 'lucide-react';
import { Issue, User, Project } from '../../types';

interface BordioDashboardProps {
    stats: () => Promise<any>;
    loadIssueBreakdown: (filters?: any) => Promise<any>;
    loadCompletionTrends: (filters?: any) => Promise<any>;
    currentUser: User | null;
    issues?: Issue[];
    projects?: Project[];
    onIssueClick?: (issue: Issue) => void;
    onCreateIssue?: () => void;
}

export const BordioDashboard: React.FC<BordioDashboardProps> = ({
    stats,
    currentUser,
    issues = [],
    projects = [],
    onIssueClick,
    onCreateIssue,
}) => {
    const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
    const [dashboardStats, setDashboardStats] = useState<any>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Load stats
    React.useEffect(() => {
        const loadStats = async () => {
            try {
                const data = await stats();
                setDashboardStats(data);
            } catch (e) {
                console.error('Failed to load stats:', e);
            } finally {
                setStatsLoading(false);
            }
        };
        loadStats();
    }, []);

    // Calculate week dates
    const weekDates = useMemo(() => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + diff + (currentWeekOffset * 7));

        return Array.from({ length: 7 }, (_, i) => {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            return date;
        });
    }, [currentWeekOffset]);

    const isToday = (date: Date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getDayName = (date: Date) => {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    // Get issues for a specific date
    const getIssuesForDate = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        return issues.filter(issue => {
            if (issue.dueDate) {
                return issue.dueDate.split('T')[0] === dateStr;
            }
            return false;
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'done': return 'bg-accent-green';
            case 'in-progress': return 'bg-accent-orange';
            case 'in-review': return 'bg-accent-purple';
            case 'to-do': return 'bg-brand-400';
            default: return 'bg-surface-400';
        }
    };

    const getPriorityIcon = (priority: string) => {
        switch (priority) {
            case 'highest':
            case 'high':
                return <Flag className="w-3 h-3 text-accent-red" />;
            case 'medium':
                return <Flag className="w-3 h-3 text-accent-orange" />;
            default:
                return <Flag className="w-3 h-3 text-surface-400" />;
        }
    };

    const weekRange = `${formatDate(weekDates[0])} - ${formatDate(weekDates[6])}`;

    // Recent tasks
    const recentTasks = useMemo(() => {
        return [...issues]
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
            .slice(0, 5);
    }, [issues]);

    // My tasks
    const myTasks = useMemo(() => {
        if (!currentUser) return [];
        return issues.filter(issue =>
            issue.assignee === currentUser.id ||
            issue.assignees?.includes(currentUser.id)
        ).filter(issue => issue.status !== 'done');
    }, [issues, currentUser]);

    // Overdue tasks
    const overdueTasks = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return issues.filter(issue => {
            if (!issue.dueDate || issue.status === 'done') return false;
            return new Date(issue.dueDate) < today;
        });
    }, [issues]);

    return (
        <div className="flex-1 overflow-auto p-6">
            {/* Welcome Section */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-surface-900">
                    Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {currentUser?.displayName?.split(' ')[0] || 'there'} 👋
                </h1>
                <p className="text-surface-500 mt-1">Here's what's happening with your projects today.</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard
                    title="Total Tasks"
                    value={dashboardStats?.total_issues || issues.length || 0}
                    icon={<Target className="w-5 h-5" />}
                    color="brand"
                    trend={dashboardStats?.issues_trend}
                    loading={statsLoading}
                />
                <StatCard
                    title="In Progress"
                    value={dashboardStats?.in_progress_issues || issues.filter(i => i.status === 'in-progress').length || 0}
                    icon={<Timer className="w-5 h-5" />}
                    color="orange"
                    loading={statsLoading}
                />
                <StatCard
                    title="Completed"
                    value={dashboardStats?.completed_issues || issues.filter(i => i.status === 'done').length || 0}
                    icon={<CheckCircle2 className="w-5 h-5" />}
                    color="green"
                    trend={dashboardStats?.completion_trend}
                    loading={statsLoading}
                />
                <StatCard
                    title="Overdue"
                    value={overdueTasks.length}
                    icon={<AlertCircle className="w-5 h-5" />}
                    color="red"
                    loading={statsLoading}
                />
            </div>

            {/* Weekly Planner */}
            <div className="bordio-card mb-6">
                <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100">
                    <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-brand-600" />
                        <h2 className="text-base font-semibold text-surface-900">Weekly Planner</h2>
                        <span className="text-sm text-surface-500">{weekRange}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentWeekOffset(0)}
                            className={`btn-ghost text-xs ${currentWeekOffset === 0 ? 'bg-brand-50 text-brand-600' : ''}`}
                        >
                            Today
                        </button>
                        <button
                            onClick={() => setCurrentWeekOffset(prev => prev - 1)}
                            className="btn-ghost p-1.5"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setCurrentWeekOffset(prev => prev + 1)}
                            className="btn-ghost p-1.5"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="planner-grid">
                    {weekDates.map((date, index) => {
                        const dayIssues = getIssuesForDate(date);
                        const today = isToday(date);

                        return (
                            <div
                                key={index}
                                className={`planner-day ${today ? 'today' : ''} ${index === 5 || index === 6 ? 'bg-surface-50/50' : ''}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className={`text-xs font-medium ${today ? 'text-brand-600' : 'text-surface-500'}`}>
                                            {getDayName(date)}
                                        </span>
                                        <span className={`text-xs font-bold ${today
                                                ? 'bg-brand-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
                                                : 'text-surface-900'
                                            }`}>
                                            {date.getDate()}
                                        </span>
                                    </div>
                                    <button
                                        onClick={onCreateIssue}
                                        className="w-5 h-5 rounded flex items-center justify-center text-surface-400 hover:text-brand-600 hover:bg-brand-50 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                    </button>
                                </div>

                                <div className="space-y-1.5">
                                    {dayIssues.slice(0, 3).map(issue => (
                                        <button
                                            key={issue.id}
                                            onClick={() => onIssueClick?.(issue)}
                                            className="w-full text-left p-2 rounded-lg bg-white border border-surface-100 hover:border-brand-200 hover:shadow-soft transition-all duration-150 group"
                                        >
                                            <div className="flex items-start gap-1.5">
                                                <div className={`status-dot mt-1.5 flex-shrink-0 ${getStatusColor(issue.status)}`} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-medium text-surface-800 line-clamp-2 leading-tight">
                                                        {issue.summary}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 mt-1">
                                                        {getPriorityIcon(issue.priority)}
                                                        <span className="text-2xs text-surface-400">{issue.key}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                    {dayIssues.length > 3 && (
                                        <p className="text-2xs text-surface-400 text-center py-1">
                                            +{dayIssues.length - 3} more
                                        </p>
                                    )}
                                    {dayIssues.length === 0 && (
                                        <div className="py-2 text-center">
                                            <p className="text-2xs text-surface-300">No tasks</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Bottom Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* My Tasks */}
                <div className="bordio-card lg:col-span-2">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-surface-900">My Tasks</h3>
                            <span className="badge badge-blue">{myTasks.length}</span>
                        </div>
                        <button className="btn-ghost text-xs">View all</button>
                    </div>
                    <div className="divide-y divide-surface-50">
                        {myTasks.length === 0 ? (
                            <div className="px-5 py-8 text-center">
                                <CheckCircle2 className="w-10 h-10 text-surface-200 mx-auto mb-2" />
                                <p className="text-sm text-surface-500">No tasks assigned to you</p>
                                <button onClick={onCreateIssue} className="text-sm text-brand-600 hover:text-brand-700 font-medium mt-1">
                                    Create a task
                                </button>
                            </div>
                        ) : (
                            myTasks.slice(0, 6).map(issue => (
                                <button
                                    key={issue.id}
                                    onClick={() => onIssueClick?.(issue)}
                                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-surface-50 transition-colors text-left"
                                >
                                    <div className={`status-dot flex-shrink-0 ${getStatusColor(issue.status)}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-surface-800 truncate">{issue.summary}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-2xs text-surface-400">{issue.key}</span>
                                            {issue.dueDate && (
                                                <span className="text-2xs text-surface-400 flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {new Date(issue.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {getPriorityIcon(issue.priority)}
                                        <span className={`badge text-2xs ${issue.status === 'done' ? 'badge-green' :
                                                issue.status === 'in-progress' ? 'badge-orange' :
                                                    issue.status === 'in-review' ? 'badge-purple' :
                                                        'badge-blue'
                                            }`}>
                                            {issue.status.replace('-', ' ')}
                                        </span>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Activity & Quick Stats */}
                <div className="space-y-6">
                    {/* Projects Overview */}
                    <div className="bordio-card">
                        <div className="px-5 py-4 border-b border-surface-100">
                            <h3 className="text-sm font-semibold text-surface-900">Projects</h3>
                        </div>
                        <div className="p-4 space-y-3">
                            {projects.length === 0 ? (
                                <div className="py-4 text-center">
                                    <p className="text-sm text-surface-500">No projects yet</p>
                                </div>
                            ) : (
                                projects.slice(0, 4).map((project, index) => {
                                    const projectIssues = issues.filter(i => i.projectId === project.id);
                                    const completedCount = projectIssues.filter(i => i.status === 'done').length;
                                    const progress = projectIssues.length > 0
                                        ? Math.round((completedCount / projectIssues.length) * 100)
                                        : 0;

                                    const colors = ['bg-brand-500', 'bg-accent-orange', 'bg-accent-green', 'bg-accent-purple'];

                                    return (
                                        <div key={project.id} className="flex items-center gap-3">
                                            <div className={`w-8 h-8 ${colors[index % colors.length]} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                                <span className="text-white text-xs font-bold">
                                                    {project.key?.charAt(0) || project.name.charAt(0)}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-surface-800 truncate">{project.name}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <div className="flex-1 h-1.5 bg-surface-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full ${colors[index % colors.length]} rounded-full transition-all duration-500`}
                                                            style={{ width: `${progress}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-2xs text-surface-500 font-medium">{progress}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Overdue Tasks */}
                    {overdueTasks.length > 0 && (
                        <div className="bordio-card border-red-100">
                            <div className="px-5 py-4 border-b border-red-50">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-accent-red" />
                                    <h3 className="text-sm font-semibold text-red-700">Overdue</h3>
                                    <span className="badge badge-red">{overdueTasks.length}</span>
                                </div>
                            </div>
                            <div className="divide-y divide-red-50">
                                {overdueTasks.slice(0, 3).map(issue => (
                                    <button
                                        key={issue.id}
                                        onClick={() => onIssueClick?.(issue)}
                                        className="w-full flex items-center gap-3 px-5 py-3 hover:bg-red-50/50 transition-colors text-left"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-surface-800 truncate">{issue.summary}</p>
                                            <p className="text-2xs text-red-500 mt-0.5">
                                                Due {new Date(issue.dueDate!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Stat Card Component
interface StatCardProps {
    title: string;
    value: number;
    icon: React.ReactNode;
    color: 'brand' | 'orange' | 'green' | 'red';
    trend?: number;
    loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend, loading }) => {
    const colorClasses = {
        brand: 'bg-brand-50 text-brand-600',
        orange: 'bg-orange-50 text-orange-600',
        green: 'bg-green-50 text-green-600',
        red: 'bg-red-50 text-red-600',
    };

    if (loading) {
        return (
            <div className="bordio-card p-5">
                <div className="flex items-center justify-between">
                    <div className="space-y-2">
                        <div className="h-3 w-20 bg-surface-100 rounded animate-pulse" />
                        <div className="h-7 w-12 bg-surface-100 rounded animate-pulse" />
                    </div>
                    <div className="w-10 h-10 bg-surface-100 rounded-xl animate-pulse" />
                </div>
            </div>
        );
    }

    return (
        <div className="bordio-card p-5 hover:shadow-elevated transition-shadow duration-200">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs font-medium text-surface-500 uppercase tracking-wide">{title}</p>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-2xl font-bold text-surface-900">{value}</p>
                        {trend !== undefined && trend !== 0 && (
                            <span className={`flex items-center gap-0.5 text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                <ArrowUpRight className={`w-3 h-3 ${trend < 0 ? 'rotate-90' : ''}`} />
                                {Math.abs(trend)}%
                            </span>
                        )}
                    </div>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
                    {icon}
                </div>
            </div>
        </div>
    );
};
